import { REWARD_TYPES_MAP } from "lib/constants";
import { REWARD_TYPES } from "lib/constants";
import * as yup from "yup";
import { parse, isAfter, isEqual, startOfToday } from "date-fns";

const REWARDS_VALIDATION_VALUES = () =>
  Array.from({ length: 20 }, (_, i) => i + 1);

const emptyStringToUndefined = (value, originalValue) => {
  return typeof originalValue === "string" && originalValue.trim() === ""
    ? undefined
    : value;
};

const ALLOWED_REWARD_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
];

export const schema = yup.object().shape({
  leaderboardType: yup
    .string()
    .trim()
    .oneOf(
      ["top_score", "top_hits", "most_games_played"],
      "Invalid leaderboard type."
    )
    .required("Leaderboard type is required."),
  rewardValue: yup
    .number()
    .integer("Decimal numers are not allowed.")
    .transform((value, originalValue) =>
      typeof originalValue === "string" && originalValue?.trim() === ""
        ? NaN
        : value
    )
    .when("rewardType", {
      is: REWARD_TYPES_MAP.GIFT_CARD,
      then: (schema) =>
        schema
          .typeError("Card amount must be a number")
          .required("Card amount is required.")
          .min(1, "Card amount cannot be zero or negative number."),
      otherwise: (schema) =>
        schema
          .typeError("Acorns quantity must be a number")
          .required("Acorns are required.")
          .min(1, "Acorns quantity cannot be zero or negative number."),
    }),
  rewardType: yup
    .string()
    .trim()
    .oneOf(REWARD_TYPES, "Invalid Reward type.")
    .required("Reward type is required."),
  cardCode: yup.string().when("rewardType", {
    is: (value) => value === REWARD_TYPES_MAP.GIFT_CARD,
    then: () =>
      yup
        .string()
        .transform(emptyStringToUndefined)
        .trim()
        .min(2, "Card code cannot be empty.")
        .required("Card code is required."),
    otherwise: () => yup.string().transform(emptyStringToUndefined).notRequired(),
    // otherwise: yup.string().notRequired(), // Skip validation otherwise
  }),
  cardType: yup
    .string()
    .nullable()
    .when("rewardType", {
      is: (value) => value === REWARD_TYPES_MAP.GIFT_CARD,
      then: () =>
        yup
          .string()
          .transform(emptyStringToUndefined)
          .trim()
          .min(2, "Card type cannot be empty.")
          .required("Card type is required."),
      otherwise: () =>
        yup.string().transform(emptyStringToUndefined).nullable().notRequired(),
    }),
  cardImage: yup
    .mixed()
    .nullable()
    .when("rewardType", {
      is: (value) => value === REWARD_TYPES_MAP.GIFT_CARD,
      then: () =>
        yup
          .mixed()
          .test(
            "gift-card-image-required",
            "Gift card image is required.",
            (value) => {
              if (!value) return false;
              if (typeof value === "string") return value.trim().length > 0;
              if (value?.length) return value.length > 0;
              return true;
            }
          )
          .test(
            "gift-card-image-type",
            "Invalid image type. Please upload a PNG, JPG, GIF, or WEBP image.",
            (value) => {
              if (!value || typeof value === "string") return true;

              const file = value?.[0] ?? value;
              if (!(file instanceof File)) return true;

              return ALLOWED_REWARD_IMAGE_TYPES.includes(file.type);
            }
          ),
      otherwise: () => yup.mixed().nullable().notRequired(),
    }),
  rank: yup
    .number()
    .typeError("Only numbers are allowed.")
    .oneOf(
      REWARDS_VALIDATION_VALUES(),
      "Only one of these (1,2,3) values are required"
    )
    .required("Rank is required."),
  rewardDate: yup
    .string()
    .required("Reward Date is required.")
    .test(
      "is-future-date",
      "Reward Date cannot be in the past.",
      function (value) {
        if (!value) return false; // Handle required case
        const parsedDate = parse(value, "yyyy-MM-dd", new Date());
        const today = startOfToday();
        return isAfter(parsedDate, today) || isEqual(parsedDate, today); // allow today or future date
      }
    ),
});
